import type { RouteLocationRaw } from 'vue-router';

// Path-as-key route target builders — same shape as src/api/index.ts
// (`api['/account/login']({ … })`).  Callers `router.push(route[path](args))`
// and the path string lives in exactly one place; renaming the route at the
// router config layer doesn't touch any caller.

export const LoginSteps = [
  'signUp',
  'account',
  'choose',
  'user',
  'sso',
  'setupName',
  'setupPassword',
  'resetPassword',
  'invitePassword',
  'info',
  // MFA login-time challenge steps (challenge kept in memory, not the URL).
  'mfaVerify',
  'mfaSetup',
] as const;
export type LoginStep = (typeof LoginSteps)[number];

export const InfoTypes = [
  'signUp',
  'noPassword',
  'sendOK',
  'sendForgetPasswordOK',
  'sendForgetAccountOK',
  'blocked',
  'checkMail',
  'resend',
] as const;
export type InfoType = (typeof InfoTypes)[number];

export type LoginQuery = {
  step?: LoginStep;
  aid?: string;
  email?: string;
  token?: string;
  id?: string;
  saml?: '1';
  oidc?: '1';
  infoType?: InfoType;
  passwordTip?: '1';
};

type PasswordLinkQuery = {
  token: string;
  email: string;
  aid: string;
};

// Marketplace `/sign-up` lands users who came from the AWS or Azure
// marketplace product page.  Backend passes the customer ID via query
// (`ampt` for AWS, `subscriptionId` for Azure); CSP is `aws` by default.
export const MarketplaceCsps = ['aws', 'azure', 'gcp'] as const;
/** @public */
export type MarketplaceCsp = (typeof MarketplaceCsps)[number];

// Centralized v3 route path constants. Components hard-needing to
// compare against a specific route (active-state highlights, redirects,
// `<RouterLink :to>`) import from here instead of inlining the string.
// Renaming a path → one PR; otherwise drift between the route record
// in `routes/*.ts` and consumer comparisons.
//
// Add a constant when it's referenced by ≥ 2 sites OR when the component
// needs to compare (not just navigate to) the path. One-shot
// `router.push('/whatever')` calls don't need to live here.
export const Path = {
  Login: '/login',
  ResetPassword: '/reset-password',
  SetupPassword: '/setup-password',
  SsoRedirect: '/sso-redirect',
  MarketplaceSignUp: '/sign-up',
  Clusters: '/clusters',
  Billing: '/billing',
  Notification: '/notification',
  McpAuthorize: '/mcp-authorize',
} as const;

export const route = {
  '/login': (query?: LoginQuery): RouteLocationRaw => ({ path: '/login', query }),
  '/reset-password': (query: PasswordLinkQuery): RouteLocationRaw => ({
    path: '/reset-password',
    query,
  }),
  '/setup-password': (query: PasswordLinkQuery): RouteLocationRaw => ({
    path: '/setup-password',
    query,
  }),
  '/sso-redirect': (query: { t: string }): RouteLocationRaw => ({
    path: '/sso-redirect',
    query,
  }),
  '/clusters': (): RouteLocationRaw => ({ path: '/clusters' }),
} as const;
